/* eslint-disable arrow-body-style */
import React, { useRef, useMemo, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { Table, TableAddon, TableColumn } from 'tea-component';
import ColumnsCenter from '@intelligence/components/TableComponents/components/CloumsCenter';
import MilestoneDateCell from '../MilestoneDateCell';
import DevBudgetPercentage from '../BudgetPercentage';
import useTbodyWidth from './_hooks/useTbodyWidth';
import { getMathValue } from '@gp/utils/common';
import { formatMilestoneDateByStatus } from '@gp/utils/milestone';
import BudgetChangeHistory, {
  BUDGET_CHANGE_HISTORY_CELL_WIDTH,
  BUDGET_CHANGE_HISTORY_CELL_SPACING,
} from './components/BudgetChangeHistory';
import { newMathFormatOfDevBudget } from '../../utils';

import type { IBaseProps } from '@home/types/common';
import type { IDevBudgetDataVO } from '@gp/types/finance';
import type { IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';

import './index.scss';

export type IMilestoneBudgetCostTableInstance = {
  /**
   * 表格滚动到Budget($) Change History列
   * 展开变更历史时，表格自动滚动到最右侧（因为变更历史列是最后一列）
   */
  scroll2ChangeHistory: () => void;
};

interface IMilestoneBudgetCostTableProps extends IBaseProps {
  isListEmpty?: boolean;
  showBudgetChangeHistory?: boolean;
  currentMilestone?: IMilestone | null;
  records?: IDevBudgetDataVO[] | null;
}

const { scrollable, autotip, injectable } = Table.addons;

const NODATA_TEXT = 'Budget Change per Milestone have not been updated at this moment.';

/**
 * 计算budget change history对应column的列宽，根据最多history来计算列宽，即取最大宽度作为列宽
 * @param records budget change history数据
 * @returns budget change history对应column的列宽
 */
const getBudgetChangeHistoryColumnWidth = (records?: IDevBudgetDataVO[] | null): number => {
  if (!Array.isArray(records) || !records.length) return 0;
  // 单个record中最大的budget change history数量
  const maxLength = Math.max(...records.map((record) => record?.budget_history?.length || 0));
  // column的内容宽度
  const budgetChangeHistoryColumnContextWidth =
    maxLength * (BUDGET_CHANGE_HISTORY_CELL_WIDTH + BUDGET_CHANGE_HISTORY_CELL_SPACING) -
    BUDGET_CHANGE_HISTORY_CELL_SPACING;
  return Math.max(budgetChangeHistoryColumnContextWidth + 20 * 2, 230);
};

const getColumnDistance = (td?: Element | null): number => {
  if (!td) return 0;
  const tdStyles = window.getComputedStyle(td);
  const tdWidth = getMathValue(tdStyles.getPropertyValue('width'));
  const tdPaddingLeft = getMathValue(tdStyles.getPropertyValue('padding-left'));
  const tdPaddingRight = getMathValue(tdStyles.getPropertyValue('padding-right'));
  const tdBorderLeft = getMathValue(tdStyles.getPropertyValue('border-left-width'));
  const tdBorderRight = getMathValue(tdStyles.getPropertyValue('border-right-width'));
  return tdWidth + tdPaddingLeft + tdPaddingRight + tdBorderLeft + tdBorderRight;
};

const getColumns = (
  tbodyWidth?: number | null,
  currentMilestone?: IMilestone | null,
  records?: IDevBudgetDataVO[] | null,
  showBudgetChangeHistory?: boolean,
): TableColumn<IDevBudgetDataVO>[] => {
  const colWidth = tbodyWidth ? tbodyWidth / 4 : 0;
  const columns: TableColumn<IDevBudgetDataVO>[] = [
    {
      key: 'milestone_id',
      header: (
        <ColumnsCenter width={360} align="left">
          <span>Milestone (Milestone Date)</span>
        </ColumnsCenter>
      ),
      width: colWidth,
      align: 'left',
      fixed: 'left',
      render: (record) => {
        const { milestone_id: milestoneId, milestone, date_confirm: dateConfirm, date } = record || {};
        const isCurrent = currentMilestone?.milestone_id === milestoneId;
        return (
          <ColumnsCenter width={360} align="left">
            <MilestoneDateCell
              isCurrent={isCurrent}
              name={milestone}
              date={date}
              dateFormatter={(payload) => formatMilestoneDateByStatus(payload, dateConfirm)}
            />
          </ColumnsCenter>
        );
      },
    },
    {
      key: 'original_budget',
      header: (
        <ColumnsCenter width={172} align="right">
          <span>Original Budget($)</span>
        </ColumnsCenter>
      ),
      width: colWidth,
      align: 'center',
      render: (record) => {
        const { original_budget: originalBudget } = record || {};
        return (
          <ColumnsCenter width={172} align="right">
            <span>{newMathFormatOfDevBudget(originalBudget)}</span>
          </ColumnsCenter>
        );
      },
    },
    {
      key: 'latest_budget',
      header: (
        <ColumnsCenter width={170} align="right">
          <span>Latest Budget($)</span>
        </ColumnsCenter>
      ),
      width: colWidth,
      align: 'center',
      render: (record) => {
        const { latest_budget: latestBudget, budget_change: budgetChange } = record || {};

        return (
          <ColumnsCenter width={170} align="right">
            <DevBudgetPercentage budget={latestBudget} budgetChange={budgetChange} />
          </ColumnsCenter>
        );
      },
    },
    {
      key: 'actual_cost',
      header: (
        <ColumnsCenter width={144} align="right">
          <span>Actual Cost($)</span>
        </ColumnsCenter>
      ),
      width: colWidth,
      align: 'center',
      render: (record) => {
        const { actual_cost: actualCost } = record || {};
        return (
          <ColumnsCenter width={144} align="right">
            <span>{newMathFormatOfDevBudget(actualCost)}</span>
          </ColumnsCenter>
        );
      },
    },
  ];
  if (showBudgetChangeHistory) {
    columns.push({
      key: 'budget_change_history',
      header: 'Budget($) Change History',
      width: getBudgetChangeHistoryColumnWidth(records),
      align: 'left',
      render: (record) => {
        const { budget_history: budgetHistory } = record || {};
        return <BudgetChangeHistory histories={budgetHistory} />;
      },
    });
  }
  return columns;
};

const MilestoneBudgetCostTable: React.ForwardRefRenderFunction<
IMilestoneBudgetCostTableInstance,
IMilestoneBudgetCostTableProps
> = (props, ref) => {
  const { style, className, isListEmpty = false, showBudgetChangeHistory = false, currentMilestone, records } = props;

  const table = useRef<HTMLDivElement>(null);

  const tbodyWidth = useTbodyWidth(table);

  const addons = useMemo<TableAddon<IDevBudgetDataVO>[]>(() => {
    return [
      scrollable({ minWidth: 1092 }),
      autotip({
        emptyText: (
          <div style={{ height: 172 }} className="dbg-flex-center">
            {NODATA_TEXT}
          </div>
        ),
      }),
      injectable({
        cell: (props, context) => {
          const { column } = context || {};
          const { key } = column || {};
          return {
            className:
              key !== 'budget_change_history'
                ? props?.className
                : classnames(props?.className, 'dbg-budget-change-history-table__highlight-cell'),
          };
        },
      }),
    ];
  }, []);

  const columns = useMemo<TableColumn<IDevBudgetDataVO>[]>(() => {
    return getColumns(tbodyWidth, currentMilestone, records, showBudgetChangeHistory);
  }, [tbodyWidth, currentMilestone, records, showBudgetChangeHistory]);

  useImperativeHandle(ref, () => ({
    scroll2ChangeHistory: () => {
      if (!table.current) return;
      if (!showBudgetChangeHistory) return;

      const tbody = table.current.querySelector('.tea-table__body');
      if (tbody) {
        const { clientWidth, scrollWidth } = tbody;
        if (scrollWidth > clientWidth) {
          const tdEls = tbody.querySelectorAll('table>tbody>tr:first-of-type>td');
          let distance = 0;
          for (let i = 0; i < tdEls.length; i++) {
            const tdEl = tdEls[i];
            // 忽略第一列，因为第一列是固定在左侧的
            if (i === 0) continue;
            // 最后一列为change history列，滚动距离计算到此为止
            if (tdEl?.classList?.contains('dbg-budget-change-history-table__highlight-cell')) continue;
            // 中间列加入滚动距离的计算
            distance += getColumnDistance(tdEl);
          }
          tbody.scrollTo({ left: distance, behavior: 'smooth' });
        }
      }
    },
  }));

  return (
    <div
      style={style}
      className={classnames(
        'dbg-project-details__finance__dev-budget-change-card__table-container dbg-budget-change-history-table-container',
        className,
      )}
    >
      <Table
        ref={table}
        className="dbg-project-details__finance__dev-budget-change-card__bordered-table dbg-budget-change-history-table"
        bordered
        recordKey="milestone_id"
        addons={addons}
        columns={columns}
        records={records || []}
      />
      {!isListEmpty && (
        <div className="dbg-project-details__finance__dev-budget-change-card__table-footer">
          {records!.length || 0} total items
        </div>
      )}
    </div>
  );
};

export default React.forwardRef(MilestoneBudgetCostTable);
